<template>
  <div class="map-root">
    <ol-map
      ref="mapRef"
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      class="map"
    >
      <ol-view ref="viewRef" :center="center" :zoom="zoom" projection="EPSG:3857" />

      <FeaturePopup ref="popupRef" :popup="popup" @close="close()" />

      <!-- Swipe overlay layer -->
      <!-- NOTE: :key must live on the ol-tile-layer elements (not the <template>);
           a key on a v-if <template> fragment is not propagated to children, so
           switching between two same-type targets would reuse the layer and leave
           the swipeReady handshake stuck (swipe control would never reappear). -->
      <template v-if="swipeTarget">
        <ol-tile-layer
          v-if="swipeTarget.layer_type === 'xyz' || swipeTarget.layer_type === 'tms'"
          :key="`swipe-xyz-${swipeTarget._key}`"
          ref="swipeTileLayerRef" :zIndex="swipeTarget.ordering" :opacity="swipeTarget.opacity"
        >
          <ol-source-xyz :url="swipeTarget.url"
            :attributions="swipeTarget.attributions || undefined"
            :tile-load-function="swipeTarget.isCog ? cogTileLoadFn : undefined" />
        </ol-tile-layer>
        <ol-tile-layer
          v-else-if="swipeTarget.layer_type === 'wms'"
          :key="`swipe-wms-${swipeTarget._key}`"
          ref="swipeTileLayerRef" :zIndex="swipeTarget.ordering" :opacity="swipeTarget.opacity"
        >
          <ol-source-tile-wms :url="swipeTarget.url"
            :params="{ LAYERS: swipeTarget.wms_layers, FORMAT: swipeTarget.wms_format || 'image/png', TILED: true }" />
        </ol-tile-layer>
        <ol-tile-layer
          v-else-if="swipeTarget.layer_type === 'wmts'"
          :key="`swipe-wmts-${swipeTarget._key}`"
          ref="swipeTileLayerRef" :zIndex="swipeTarget.ordering" :opacity="swipeTarget.opacity"
        >
          <ol-source-wmts :url="swipeTarget.url" />
        </ol-tile-layer>
        <ol-swipe-control v-if="swipeReady && swipeOlLayer" :layers="[swipeOlLayer]" />
      </template>

      <!-- Active raster layers (static tile layers + selected COG tiles) -->
      <template v-for="layer in activeRasterLayers" :key="layer._key">
        <ol-tile-layer v-if="layer.layer_type === 'xyz' || layer.layer_type === 'tms'"
          :zIndex="layer.ordering" :opacity="layer.opacity">
          <ol-source-xyz :url="layer.url"
            :attributions="layer.attributions || undefined"
            :min-zoom="layer.min_zoom" :max-zoom="layer.max_zoom"
            :tile-load-function="layer.isCog ? cogTileLoadFn : undefined" />
        </ol-tile-layer>
        <ol-tile-layer v-else-if="layer.layer_type === 'wms'" :zIndex="layer.ordering" :opacity="layer.opacity">
          <ol-source-tile-wms :url="layer.url"
            :params="{ LAYERS: layer.wms_layers, FORMAT: layer.wms_format || 'image/png', TILED: true }"
            :attributions="layer.attributions || undefined" />
        </ol-tile-layer>
        <ol-tile-layer v-else-if="layer.layer_type === 'wmts'" :zIndex="layer.ordering" :opacity="layer.opacity">
          <ol-source-wmts :url="layer.url" />
        </ol-tile-layer>
      </template>

      <!-- Active vector layers (year-filtered, checked-visible VectorLayerPages) -->
      <template v-for="layer in activeVectorLayers" :key="`vector-${layer.id}`">
        <ol-vector-layer
          :ref="(comp: any) => { if (comp?.vectorLayer) olLayerToMeta.set(comp.vectorLayer, { popup_template: layer.popup_template }) }"
          :zIndex="vectorZIndex(layer)" :opacity="layer.opacity"
        >
          <ol-source-vector :url="layer.source_url" :format="geoJsonFormat" />
          <ol-style>
            <ol-style-stroke :color="layer.stroke_color" :width="layer.stroke_width" />
            <ol-style-fill :color="layer.fill_color" />
            <ol-style-circle v-if="layer.geometry_type === 'point'" :radius="layer.point_radius">
              <ol-style-fill :color="layer.fill_color" />
              <ol-style-stroke :color="layer.stroke_color" :width="layer.stroke_width" />
            </ol-style-circle>
          </ol-style>
        </ol-vector-layer>
      </template>

      <ol-zoom-control />
    </ol-map>

    <MapInfoPanel />
    <RightPanel :layer-tree="layerTree" :loading="loading" :error="error" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, shallowRef, watch, nextTick } from 'vue'
import { GeoJSON } from 'ol/format'
import { useFarms } from '@/composables/useFarms'
import { useLayerSelection } from '@/composables/useLayerSelection'
import { descendScope, type SelectionScope } from '@/composables/layerScope'
import type { ActiveRasterLayer, ActiveVectorLayer, LayerTreeNode } from '@/types/layerTree'
import MapInfoPanel from '@/components/MapInfoPanel.vue'
import RightPanel from '@/components/RightPanel.vue'
import type { Layer } from 'ol/layer'
import { useFeaturePopup } from '@/composables/useFeaturePopup'
import type { VectorLayerMeta } from '@/composables/useFeaturePopup'
import FeaturePopup from '@/components/FeaturePopup.vue'

// ── Map defaults ──────────────────────────────────────────────────────────
const center = ref([1900000, 5700000])
const zoom   = ref(6)

// ── Farm + layer tree ─────────────────────────────────────────────────────
const { farms, activeFarm, activeField, layerTree, loading, error, fetchFarms, selectFarm } = useFarms()

// ── Layer selection state (shared singleton) ───────────────────────────────
const {
  selectedCogByNode,
  activeNodesByGroup,
  visibleVectorLayerIds,
  swipeTarget,
  selectedYears,
  hiddenGroupNodeIds,
  resetForFarmChange,
  activateNode,
  selectCog,
  toggleVectorLayer,
  pendingZoomExtent,
  clearZoomExtent,
} = useLayerSelection()

// ── Derived: what OL renders ───────────────────────────────────────────────

const activeRasterLayers = computed<ActiveRasterLayer[]>(() => {
  const out: ActiveRasterLayer[] = []
  traverseRaster(layerTree.value, out)
  return out
})

function traverseRaster(nodes: LayerTreeNode[], out: ActiveRasterLayer[], scope?: SelectionScope) {
  for (const node of nodes) {
    if (node.type === 'group') {
      if (hiddenGroupNodeIds.value.has(node.id)) continue
      traverseRaster(node.children, out, descendScope(scope, node))
    } else if (node.type === 'raster_layer') {
      if (scope == null || !activeNodesByGroup.value.get(scope.scopeId)?.has(node.id)) continue
      // NOTE: active rendering is intentionally independent of the swipe target.
      // If the active layer is also chosen as the swipe layer it renders twice
      // (full + swipe overlay of identical content — visually a no-op), but the
      // selection state stays consistent. Coupling these caused the swipe control
      // to churn active layers and corrupt exclusive selection.
      const rl = node.raster_layer
      out.push({
        _key: `rl-${node.id}`,
        layer_type: rl.layer_type,
        url: rl.url,
        wms_layers: rl.wms_layers || undefined,
        wms_format: rl.wms_format || undefined,
        attributions: rl.attributions || undefined,
        min_zoom: rl.min_zoom,
        max_zoom: rl.max_zoom,
        opacity: rl.opacity,
        ordering: rl.ordering,
      })
    } else if (node.type === 'raster_type') {
      if (scope == null || !activeNodesByGroup.value.get(scope.scopeId)?.has(node.id)) continue
      const cogId = selectedCogByNode.value.get(node.id)
      if (cogId != null) {
        const cog = node.cogs.find(c => c.id === cogId)
        if (cog) {
          // Active rendering stays independent of the swipe target — see note in
          // the raster_layer branch above.
          const year = new Date(cog.sensing_date).getFullYear()
          if (!selectedYears.value.size || selectedYears.value.has(year)) {
            const style = node.raster_type.titiler_style_string
            const url = style ? `${cog.tile_url}?${style}` : cog.tile_url
            out.push({ _key: `cog-${cog.id}`, isCog: true, layer_type: 'xyz', url, opacity: 1, ordering: 500 })
          }
        }
      }
    }
  }
}

const activeVectorLayers = computed<ActiveVectorLayer[]>(() => {
  const out: ActiveVectorLayer[] = []
  traverseVector(layerTree.value, out)
  return out
})

function traverseVector(nodes: LayerTreeNode[], out: ActiveVectorLayer[], scope?: SelectionScope) {
  for (const node of nodes) {
    if (node.type === 'group') {
      if (hiddenGroupNodeIds.value.has(node.id)) continue
      traverseVector(node.children, out, descendScope(scope, node))
    } else if (node.type === 'vector' && node.schema) {
      if (scope == null || !activeNodesByGroup.value.get(scope.scopeId)?.has(node.id)) continue
      const s = node.schema
      for (const vl of node.vector_layers) {
        if (!visibleVectorLayerIds.value.has(vl.id)) continue
        if (selectedYears.value.size && !selectedYears.value.has(vl.year)) continue
        out.push({
          id: vl.id,
          source_url: vl.source_url,
          geometry_type: vl.geometry_type,
          order: s.order,
          popup_template: s.popup_template,
          stroke_color: s.stroke_color,
          fill_color: s.fill_color,
          stroke_width: s.stroke_width,
          point_radius: s.point_radius,
          opacity: s.opacity,
        })
      }
    }
  }
}

// ── Vector draw order ──────────────────────────────────────────────────────
// Vectors render above all rasters (base 10000). An explicit `order` from the
// schema wins; otherwise geometry type decides, so points always sit above
// polygons (and lines in between). This keeps point features clickable when a
// polygon layer overlaps them — forEachFeatureAtPixel returns top zIndex first.
const VECTOR_Z_BASE = 10000
const GEOMETRY_Z: Record<ActiveVectorLayer['geometry_type'], number> = {
  polygon: 0,
  mixed: 10,
  line: 20,
  point: 30,
}

function vectorZIndex(layer: ActiveVectorLayer): number {
  if (layer.order != null) return VECTOR_Z_BASE + layer.order
  return VECTOR_Z_BASE + (GEOMETRY_Z[layer.geometry_type] ?? 0)
}

// ── COG tile loader (attaches JWT — OL doesn't use Axios) ─────────────────
function cogTileLoadFn(tile: any, src: string) {
  const token = localStorage.getItem('access')
  fetch(src, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
    .then(res => {
      if (!res.ok) throw new Error(`${res.status}`)
      return res.blob()
    })
    .then(blob => { tile.getImage().src = URL.createObjectURL(blob) })
    .catch(() => tile.setState(3))  // OL TileState.ERROR
}

// ── Swipe control ─────────────────────────────────────────────────────────
const swipeTileLayerRef = shallowRef<any>(null)
const swipeReady        = ref(false)

// Pre-render: drop swipeReady so the swipe control isn't created mid-transition.
// (Does NOT touch swipeTileLayerRef — that caused the original race condition.)
watch(swipeTarget, () => {
  swipeReady.value = false
})

// Post-render: set swipeReady only after the new tile layer's ref is settled.
watch(swipeTileLayerRef, async (comp) => {
  if (!comp?.tileLayer) return
  await nextTick()
  swipeReady.value = true
}, { flush: 'post' })

const swipeOlLayer = computed<Layer | null>(() => swipeTileLayerRef.value?.tileLayer ?? null)

// ── Map refs ───────────────────────────────────────────────────────────────
const mapRef  = shallowRef<any>(null)
const viewRef = shallowRef<any>(null)

// ── Zoom to extent helper ─────────────────────────────────────────────────
function zoomToFarm() {
  const extent = activeFarm.value?.extent
  if (!extent) return
  const olView = viewRef.value?.view
  if (!olView) return
  olView.fit(extent, {
    padding: [4, 4, 4, 4],
    duration: 600,
  })
}

// ── Zoom to COG bounds (triggered by RasterTypeControl) ──────────────────
// Extent is stored in EPSG:3857 (already converted by the component).
watch(pendingZoomExtent, (extent) => {
  if (!extent) return
  const olView = viewRef.value?.view
  if (!olView) return
  olView.fit(extent, { padding: [60, 60, 60, 60], duration: 700 })
  clearZoomExtent()
})

// ── Default active layers ──────────────────────────────────────────────────
// Turn on any leaf flagged `default_active` by the backend (a group's
// `default_active_layer`). For raster_type / vector also pick the most recent
// COG / year so the layer actually renders, not just gets selected.
function applyDefaultActiveLayers(nodes: LayerTreeNode[], scope?: SelectionScope) {
  for (const node of nodes) {
    if (node.type === 'group') {
      applyDefaultActiveLayers(node.children, descendScope(scope, node))
      continue
    }
    if (scope == null || !node.default_active) continue

    // An exclusive scope can now span several sub-groups, so it may collect more
    // `default_active` leaves than it can hold. First in tree order wins (the
    // backend sorts children by `ordering`), rather than whichever happens to be
    // visited last.
    if (scope.exclusive && activeNodesByGroup.value.get(scope.scopeId)?.size) continue

    // allowToggleOff=false: defaults only ever switch layers on.
    activateNode(scope.scopeId, node.id, scope.exclusive, false)
    if (node.type === 'raster_type' && node.cogs.length) {
      const latest = node.cogs.reduce((a, b) => (a.sensing_date >= b.sensing_date ? a : b))
      selectCog(node.id, latest.id)
    } else if (node.type === 'vector' && node.vector_layers.length) {
      const latest = node.vector_layers.reduce((a, b) => (a.year >= b.year ? a : b))
      toggleVectorLayer(latest.id, true)
    }
  }
}

// ── When active farm changes: reset selections, apply defaults, zoom ───────
watch(() => activeFarm.value?.id, async (newId, oldId) => {
  if (newId === oldId) return
  resetForFarmChange()
  applyDefaultActiveLayers(layerTree.value)
  await nextTick()
  zoomToFarm()
})

// ── When active field changes: re-scope the tree, reset, apply defaults, zoom ─
// A field switch keeps the same farm id, so the farm watcher above won't fire.
watch(() => activeField.value?.id, async (newId, oldId) => {
  if (!newId || newId === oldId) return
  resetForFarmChange()
  applyDefaultActiveLayers(layerTree.value)
  await nextTick()
  const olView = viewRef.value?.view
  const extent = activeField.value?.extent ?? activeFarm.value?.extent
  if (olView && extent) {
    olView.fit(extent, { padding: [40, 40, 40, 40], duration: 600 })
  }
})

// ── Feature popup ──────────────────────────────────────────────────────────
const popupRef = ref<InstanceType<typeof FeaturePopup> | null>(null)
const olLayerToMeta = new WeakMap<Layer, VectorLayerMeta>()

const geoJsonFormat = computed(() => new GeoJSON())

const { popup, mount, close } = useFeaturePopup(
  mapRef,
  computed(() => popupRef.value?.el ?? null),
  (olLayer: Layer) => olLayerToMeta.get(olLayer),
)

// ── Initialise ─────────────────────────────────────────────────────────────
onMounted(async () => {
  await fetchFarms()

  if (!activeFarm.value && farms.value.length === 1 && farms.value[0]) {
    await selectFarm(farms.value[0].id)
  }

  const olMap = mapRef.value?.map
  if (olMap) mount(olMap)

  zoomToFarm()
})
</script>

<style scoped>
.map-root {
  position: relative;
  width: 100%;
  height: 100%;
}
.map {
  width: 100%;
  height: 100%;
}
</style>
